import { createRef, forwardRef, useCallback, useMemo, useEffect } from 'react';
import type { Ref, PropsWithoutRef, ReactNode, MouseEventHandler } from 'react';

import { focusHeadingOrContainer } from '../../utils';
import { useConfiguration, useConsolidatedRef, useUID } from '../../hooks';
import Button from '../Button';
import type { ButtonProps } from '../Button';
import Flex from '../Flex';
import Icon from '../Icon';
import MenuButton from '../MenuButton';
import Text from '../Text';
import Progress from '../Progress';
import type { Action, ForwardProps, ForwardRefForwardPropsComponent } from '../../types';

import {
  StyledTemplate,
  StyledTemplateRow,
  StyledPageArticle,
  StyledArticleHeader,
  StyledPageActionCol,
  StyledPageTitle,
  StyledMenuColumn,
  StyledCategoryHeadingRow,
  StyledCategoryIconBlock,
  StyledNav,
  StyledNavList,
  StyledNavGroup,
  StyledNavGroupList,
  StyledNavGroupHeader,
  StyledNavItem,
  StyledSkipNavigation
} from './CategorySubPage.styles';

export interface NavItemType extends ForwardProps {
  text: string;
  active?: boolean;
  onClick?: MouseEventHandler;
  href?: string;
}

export interface NavGroupType {
  title?: string;
  items: NavItemType[];
}

export interface PageAction
  extends Pick<Action, 'id' | 'icon' | 'href' | 'onClick' | 'disabled'>,
    Pick<ButtonProps, 'variant' | 'label' | 'aria-label' | 'loading'>,
    ForwardProps {
  /** Optional text to replace required text string in Action type */
  text?: string;
}

export interface TemplateProps {
  /** Indicates that the primary view for the page cannot yet be rendered */
  /** @default false */
  viewLoading?: boolean;
  /** Parent Category of page and all navigation items */
  category: string;
  /** Parent Category icon */
  categoryIcon?: string;
  navItemGroups: NavGroupType[];
  pageTitle?: string;
  /** Actions to render in fixed page header. Ordered by priority */
  pageActions?: PageAction[];
  /** Maximum top-level action buttons to render in header. Actions passed exceeding this limit will render in an action menu. */
  /** @default 3 */
  maxActions?: number;
  /** Primary page content region */
  region: ReactNode;
  /** Ref forwarded to the wrapping element. */
  ref?: Ref<HTMLDivElement>;
}

const NavItem = ({ item: { href, text, active, ...restProps } }: { item: NavItemType }) => {
  const isAnchor = !!href;

  return (
    <StyledNavItem
      as='li'
      selected={!!active}
      tabType='horizontal'
      aria-current={active ? 'page' : undefined}
    >
      <Button {...restProps} href={isAnchor ? href : undefined} variant='text'>
        {text}
      </Button>
    </StyledNavItem>
  );
};

const NavGroup = ({ item }: { item: NavGroupType }) => {
  const mappedGroupedItems = useMemo(() => {
    return item.items.map(groupedItem => {
      const key = groupedItem.text;
      return <NavItem item={groupedItem} key={key} />;
    });
  }, [item.items]);

  const titledGroup: boolean = useMemo(() => {
    return !!item.title && !!item.items.length;
  }, [item.title, item.items]);

  const anonGroup: boolean = useMemo(() => {
    return !item.title && item.items.length > 1;
  }, [item.title, item.items]);

  const groupLabelId = useUID();
  if (titledGroup) {
    return (
      <StyledNavGroup>
        <StyledNavGroupHeader variant='h2' id={groupLabelId}>
          {(item as NavGroupType).title}
        </StyledNavGroupHeader>

        <StyledNavGroupList aria-labelledby={groupLabelId}>{mappedGroupedItems}</StyledNavGroupList>
      </StyledNavGroup>
    );
  }

  if (anonGroup) {
    return (
      <StyledNavGroup>
        <ul>{mappedGroupedItems}</ul>
      </StyledNavGroup>
    );
  }

  if (item.items.length === 1) {
    return <NavItem item={item.items[0]} />;
  }

  return null;
};

const CategorySubPage: ForwardRefForwardPropsComponent<TemplateProps> = forwardRef(
  function CategorySubPage(
    {
      viewLoading = false,
      category,
      categoryIcon,
      navItemGroups,
      pageTitle,
      pageActions,
      region,
      maxActions = 3,
      ...restProps
    }: PropsWithoutRef<TemplateProps>,
    ref: TemplateProps['ref']
  ) {
    const articleRef = createRef<HTMLElement>();
    const templateRef = useConsolidatedRef(ref);
    const { loadedRef } = useConfiguration();

    useEffect(() => {
      if (!templateRef.current) return;

      if (loadedRef.current) {
        focusHeadingOrContainer(templateRef.current, category);
      } else {
        loadedRef.current = true;
      }
    }, [category]);

    const mappedAction = useCallback((action: PageAction) => {
      const { id, text, icon, label, ...rest } = action;

      return (
        <Button {...rest} key={id} icon={!!icon && !text} label={label || text}>
          {!!icon && <Icon name={icon} />}
          {!!text && text}
        </Button>
      );
    }, []);

    return (
      <StyledTemplate>
        <Flex
          {...restProps}
          ref={templateRef}
          as={StyledTemplateRow}
          container={{ alignItems: 'stretch', wrap: 'nowrap' }}
        >
          <StyledMenuColumn>
            <StyledSkipNavigation
              onClick={() => {
                if (articleRef.current) articleRef.current.focus();
              }}
            >
              Skip to page content
            </StyledSkipNavigation>

            <Flex as={StyledCategoryHeadingRow} container={{ alignItems: 'center' }}>
              {categoryIcon && (
                <Flex
                  as={StyledCategoryIconBlock}
                  container={{ alignItems: 'center', justify: 'center' }}
                >
                  <Icon name={categoryIcon} />
                </Flex>
              )}
              <Text variant='h1'>{category}</Text>
            </Flex>

            <StyledNav>
              <StyledNavList>
                {navItemGroups.map((navItem, index) => {
                  const key = 'title' in navItem ? navItem.title : `navItem${index}`;

                  return <NavGroup key={key} item={navItem} />;
                })}
              </StyledNavList>
            </StyledNav>
          </StyledMenuColumn>

          <Flex
            as={StyledPageArticle}
            item={{ grow: 1 }}
            ref={articleRef}
            aria-label={pageTitle || 'Main Content'}
            tabIndex={0}
          >
            {viewLoading && <Progress placement='local' />}

            {!viewLoading && (
              <>
                {(pageTitle || pageActions) && (
                  <Flex
                    as={StyledArticleHeader}
                    container={{
                      justify: 'between',
                      alignItems: 'center',
                      wrap: 'wrap'
                    }}
                  >
                    {pageTitle && <StyledPageTitle>{pageTitle}</StyledPageTitle>}

                    {pageActions && !!pageActions.length && (
                      <>
                        {pageActions.length <= maxActions && (
                          <StyledPageActionCol>
                            {pageActions.reverse().map(mappedAction)}
                          </StyledPageActionCol>
                        )}

                        {pageActions.length > maxActions && (
                          <StyledPageActionCol>
                            <MenuButton
                              text='Actions'
                              menu={{
                                items: (pageActions as Action[])
                                  .slice(maxActions - 1)
                                  .map(({ id, text, onClick, ...rest }) => ({
                                    id,
                                    primary: text,
                                    onClick,
                                    ...rest
                                  }))
                              }}
                            />

                            {pageActions
                              .slice(0, maxActions - 1)
                              .reverse()
                              .map(mappedAction)}
                          </StyledPageActionCol>
                        )}
                      </>
                    )}
                  </Flex>
                )}

                {region}
              </>
            )}
          </Flex>
        </Flex>
      </StyledTemplate>
    );
  }
);

export default CategorySubPage;
